import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAxios, setCurrentAccountUniqueIdentifierInLocalStorage, getCurrentAccountUniqueIdentifierInLocalStorage } from "../../core/_axios";
import { useAuth } from "../../AuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import usePageTitle from "../../hooks/usePageTitle";

interface ReceivedEmailDetail {
  id: string;
  from: string;
  to: string;
  subject: string;
  text_content: string;
  html_content: string;
  message_id: string;
  date: string;
  status: string;
  attachments: Array<{
    filename: string;
    content_type: string;
    size?: number;
  }>;
  received_at: string;
  s3_bucket?: string;
  s3_key?: string;
  inserted_at: string;
  updated_at: string;
  linked_services_count?: number;
  account?: {
    unique_identifier: string;
    name: string;
  };
}

interface LinkedService {
  id: string;
  unique_identifier: string;
  deceased_name: string;
  service_date: string;
  signature_count: number;
}

const ReceivedEmailDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const axios = useAxios();
  const { viewingAllAccounts } = useAuth();
  
  const [email, setEmail] = useState<ReceivedEmailDetail | null>(null);
  const [linkedServices, setLinkedServices] = useState<LinkedService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [sentReplies, setSentReplies] = useState<Array<{
    content: string;
    sentAt: Date;
    to: string;
  }>>([]);
  
  usePageTitle(email ? `${email.subject || '(no subject)'}` : 'Email');

  useEffect(() => {
    if (id) {
      fetchEmail();
    }
  }, [id]);

  const fetchEmail = async () => {
    setLoading(true);
    setError(null);
    
    // Helper function to temporarily switch account context
    const withAccountContext = async (accountId: string | null, fn: () => Promise<any>) => {
      const originalAccount = getCurrentAccountUniqueIdentifierInLocalStorage();
      try {
        if (accountId && viewingAllAccounts) {
          setCurrentAccountUniqueIdentifierInLocalStorage(accountId);
        }
        return await fn();
      } finally {
        if (accountId && viewingAllAccounts) {
          setCurrentAccountUniqueIdentifierInLocalStorage(originalAccount || '');
        }
      }
    };

    try {
      // If we're viewing all accounts and don't have the email yet, 
      // we need to make a special API call to get the email with account info
      if (viewingAllAccounts) {
        // First get the email list to find this email's account
        const listResponse = await axios.get('/api/v1/app/authenticated/current-account/received-emails?all_accounts=true');
        const emailInList = listResponse.data.emails.find((e: any) => e.id === id);
        
        if (emailInList?.account?.unique_identifier) {
          // Now fetch the full email details using the account context
          await withAccountContext(emailInList.account.unique_identifier, async () => {
            const response = await axios.get(`/api/v1/app/authenticated/current-account/received-emails/${id}`);
            setEmail({
              ...response.data.email,
              account: emailInList.account // Ensure account info is preserved
            });
            setLinkedServices(response.data.linked_services || []);
            if (response.data.sent_replies) {
              setSentReplies(response.data.sent_replies.map((reply: any) => ({
                content: reply.text_content,
                sentAt: new Date(reply.sent_at),
                to: extractSenderEmail(reply.to)
              })));
            }
          });
          return;
        }
      }
      
      // Standard flow for specific account view
      const response = await axios.get(`/api/v1/app/authenticated/current-account/received-emails/${id}`);
      setEmail(response.data.email);
      setLinkedServices(response.data.linked_services || []);
      if (response.data.sent_replies) {
        setSentReplies(response.data.sent_replies.map((reply: any) => ({
          content: reply.text_content,
          sentAt: new Date(reply.sent_at),
          to: extractSenderEmail(reply.to)
        })));
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError("Email not found");
      } else {
        setError("Failed to load email");
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!email || !id) return;
    
    // Helper function to temporarily switch account context  
    const withAccountContext = async (accountId: string | null, fn: () => Promise<any>) => {
      const originalAccount = getCurrentAccountUniqueIdentifierInLocalStorage();
      try {
        if (accountId && viewingAllAccounts) {
          setCurrentAccountUniqueIdentifierInLocalStorage(accountId);
        }
        return await fn();
      } finally {
        if (accountId && viewingAllAccounts) {
          setCurrentAccountUniqueIdentifierInLocalStorage(originalAccount || '');
        }
      }
    };
    
    try {
      await withAccountContext(email.account?.unique_identifier || null, async () => {
        await axios.put(`/api/v1/app/authenticated/current-account/received-emails/${id}`, {
          status: newStatus
        });
      });
      
      setEmail({ ...email, status: newStatus });
    } catch (err) {
      console.error("Failed to update email status", err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const extractSenderName = (from: string) => {
    const match = from.match(/^([^<]+)\s*</);
    if (match) {
      return match[1].trim();
    }
    const emailMatch = from.match(/^([^@]+)@/);
    return emailMatch ? emailMatch[1] : from;
  };

  const extractSenderEmail = (from: string) => {
    const match = from.match(/<([^>]+)>/);
    if (match) {
      return match[1];
    }
    return from;
  };

  const handleReply = () => {
    if (!email) return;
    
    // Don't pre-populate - let user write fresh reply
    setReplyContent('');
    setReplyError(null);
    setShowReplyComposer(true);
  };

  const handleSendReply = async () => {
    if (!email || !id) return;
    
    if (!replyContent.trim()) {
      setReplyError('Please enter a message before sending.');
      return;
    }
    
    // Helper function to temporarily switch account context
    const withAccountContext = async (accountId: string | null, fn: () => Promise<any>) => {
      const originalAccount = getCurrentAccountUniqueIdentifierInLocalStorage();
      try {
        if (accountId && viewingAllAccounts) {
          setCurrentAccountUniqueIdentifierInLocalStorage(accountId);
        }
        return await fn();
      } finally {
        if (accountId && viewingAllAccounts) {
          setCurrentAccountUniqueIdentifierInLocalStorage(originalAccount || '');
        }
      }
    };
    
    setSending(true);
    setReplyError(null);
    try {
      const response = await withAccountContext(email.account?.unique_identifier || null, async () => {
        return await axios.post(`/api/v1/app/authenticated/current-account/received-emails/${id}/reply`, {
          content: replyContent
        });
      });
      
      if (response.data.success) {
        // Show success message
        const successDiv = document.createElement('div');
        successDiv.className = 'alert alert-success alert-dismissible';
        successDiv.innerHTML = `
          Reply sent successfully!
          <button type="button" class="btn btn-close" aria-label="Close"></button>
        `;
        
        const container = document.querySelector('.container-fixed');
        if (container) {
          container.insertBefore(successDiv, container.firstChild);
          
          // Auto-dismiss after 5 seconds
          setTimeout(() => successDiv.remove(), 5000);
          
          // Manual dismiss
          const closeBtn = successDiv.querySelector('.btn-close');
          if (closeBtn) {
            closeBtn.addEventListener('click', () => successDiv.remove());
          }
        }
        
        // Add the reply to our local state
        setSentReplies(prev => [...prev, {
          content: replyContent,
          sentAt: new Date(),
          to: extractSenderEmail(email.from)
        }]);
        
        setShowReplyComposer(false);
        setReplyContent("");
      }
    } catch (err: any) {
      console.error("Failed to send reply", err);
      const errorMessage = err.response?.data?.error || 'Failed to send reply. Please try again.';
      setReplyError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !email) {
    return (
      <div className="container-fixed">
        <div className="alert alert-danger mb-4">
          <div className="flex items-center justify-between">
            <span>{error || "Email not found"}</span>
          </div>
        </div>
        <div className="flex gap-3">
          <button 
            className="btn btn-primary"
            onClick={() => fetchEmail()}
          >
            <i className="ki-filled ki-arrows-circle mr-2"></i>
            Retry
          </button>
          <button 
            className="btn btn-light"
            onClick={() => navigate(-1)}
          >
            <i className="ki-filled ki-arrow-left mr-2"></i>
            Go Back
          </button>
          <button 
            className="btn btn-light"
            onClick={() => navigate('/emails/received/all')}
          >
            <i className="ki-filled ki-message-text-2 mr-2"></i>
            Back to Inbox
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fixed">
      {/* Email Header */}
      <div className="card mb-5">
        <div className="card-header">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <button 
                className="btn btn-sm btn-icon btn-light btn-icon-lg"
                onClick={() => navigate('/emails/received/all')}
              >
                <i className="ki-filled ki-arrow-left"></i>
              </button>
              <h2 className="card-title text-lg mb-0">
                {email.subject || "(no subject)"}
              </h2>
            </div>
            
            <div className="flex items-center gap-1">
              <button
                className="btn btn-sm btn-primary"
                onClick={handleReply}
                title="Reply to this email"
              >
                <i className="ki-filled ki-message-text mr-2"></i>
                Reply
                {viewingAllAccounts && email.account && (
                  <span className="ml-1 text-xs opacity-80">as {email.account.name}</span>
                )}
              </button>
              
              {email.status !== 'archived' && (
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => handleStatusChange('archived')}
                  title="Archive"
                >
                  <i className="ki-filled ki-archive mr-2"></i>
                  Archive
                </button>
              )}
              
              <button
                className="btn btn-sm btn-light"
                onClick={() => handleStatusChange(email.status === 'unread' ? 'read' : 'unread')}
                title={`Mark as ${email.status === 'unread' ? 'Read' : 'Unread'}`}
              >
                <i className={`ki-filled ${email.status === 'unread' ? 'ki-sms' : 'ki-message-text-2'} mr-2`}></i>
                Mark as {email.status === 'unread' ? 'Read' : 'Unread'}
              </button>
            </div>
          </div>
        </div>
        
        <div className="card-body">
          {/* Sender Info */}
          <div className="mb-5 pb-5 border-b">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="size-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-600 font-semibold">
                      {extractSenderName(email.from).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold">{extractSenderName(email.from)}</div>
                    <div className="text-sm text-gray-600">{extractSenderEmail(email.from)}</div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  To: {email.to}
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {formatDate(email.received_at)}
              </div>
            </div>
          </div>

          {/* Attachments */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="mb-5 pb-5 border-b">
              <h4 className="text-sm font-semibold mb-3">
                Attachments ({email.attachments.length})
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {email.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                    <i className="ki-filled ki-document text-gray-500"></i>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{attachment.filename}</div>
                      <div className="text-xs text-gray-600">
                        {attachment.content_type}
                        {attachment.size && ` • ${Math.round(attachment.size / 1024)}KB`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Linked Services */}
          {linkedServices.length > 0 && (
            <div className="mb-5 pb-5 border-b">
              <h4 className="text-sm font-semibold mb-3">
                Linked Services ({linkedServices.length})
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {linkedServices.map((service) => (
                  <a
                    key={service.unique_identifier}
                    href={`/services/${service.unique_identifier}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <i className="ki-filled ki-profile-user text-gray-500"></i>
                      <div>
                        <div className="font-medium text-sm">{service.deceased_name}</div>
                        <div className="text-xs text-gray-600">
                          Service Date: {new Date(service.service_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500">
                        {service.signature_count} signature{service.signature_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Email Content */}
          <div className="email-content">
            {email.html_content ? (
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: email.html_content }}
              />
            ) : email.text_content ? (
              <div className="whitespace-pre-wrap font-sans text-sm">
                {email.text_content}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No content available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sent Replies */}
      {sentReplies.length > 0 && (
        <div className="card mb-5">
          <div className="card-header">
            <h3 className="card-title text-base">Your Replies</h3>
          </div>
          <div className="card-body space-y-4">
            {sentReplies.map((reply, index) => (
              <div key={index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="size-8 bg-primary-light rounded-full flex items-center justify-center">
                      <span className="text-primary text-sm font-semibold">You</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold">You</div>
                      <div className="text-xs text-gray-500">To: {reply.to}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(reply.sentAt.toISOString())}
                  </div>
                </div>
                <div className="whitespace-pre-wrap text-sm pl-11">
                  {reply.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reply Composer */}
      {showReplyComposer && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Reply</h3>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-1">To: {extractSenderEmail(email.from)}</div>
              <div className="text-sm text-gray-600">Subject: Re: {email.subject || "(no subject)"}</div>
            </div>
            
            <textarea
              className="input w-full min-h-[200px] resize-y p-3"
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Type your reply..."
              autoFocus
            />
          </div>
          <div className="card-footer">
            {replyError && (
              <div className="alert alert-danger mb-4">
                {replyError}
              </div>
            )}
            <div className="flex gap-3">
              <button
                className="btn btn-primary"
                onClick={handleSendReply}
                disabled={!replyContent.trim() || sending}
              >
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
              <button
                className="btn btn-light"
                onClick={() => {
                  setShowReplyComposer(false);
                  setReplyContent("");
                  setReplyError(null);
                }}
                disabled={sending}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedEmailDetail;